import { useSelector } from '@xstate/react';
import { DocMemo, getParsedDocument } from 'models/Memo';
import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import type { JSONContent } from '@tiptap/react';
import { Draft } from 'xstate/machines/DraftMachine';
import { DraftActor } from 'xstate/actors/DraftActor';

interface UseEditorResult {
  draftState: ReturnType<typeof DraftActor.getSnapshot>;
  newDraft: Draft;
  drafts: Record<string, Draft>;
  currentDraft?: Draft;
  hasDraftContent: boolean;
  hasDraftChanged: boolean;
}

export const useDraft = (memo?: DocMemo): UseEditorResult => {
  const draftState = useSelector(DraftActor, (snapshot) => snapshot);

  // Draft for a new memo
  const newDraft = draftState.context.newDraft;

  // Draft for existing memos
  const drafts = draftState.context.drafts;

  // If a memo is provided, it should look for the correct
  // draft by memo id, if none is found, default to draft for a new memo
  const currentDraft = useMemo(() => {
    if (Boolean(memo) && memo.id) {
      return drafts[memo.id];
    }
    return newDraft;
  }, [memo, drafts, newDraft]);

  // Whether the current draft has any content
  const hasDraftContent = useMemo(() => {
    if (!currentDraft) return false;

    // If there is a draft but it's empty
    if (currentDraft.document?.content?.length === 0) {
      return false;
    }

    // If there is a draft, but the first and only element is empty
    if (
      currentDraft.document?.content?.length === 1 &&
      currentDraft.document?.content[0]?.content === undefined
    ) {
      return false;
    }

    return true;
  }, [currentDraft]);

  // Whether the current draft has changes compared to the saved memo
  const hasDraftChanged = useMemo(() => {
    // If we're creating a new memo (no memo provided) or there's no draft, there are no changes
    if (!currentDraft || !memo || !memo.document) return false;

    // If we couldn't parse the document, there are no changes
    const memoDocument = getParsedDocument<JSONContent>(memo);
    if (!memoDocument) return false;

    // Compare the draft document with the memo document
    return !isEqual(currentDraft.document, memoDocument);
  }, [currentDraft, memo]);

  return {
    draftState,
    newDraft,
    drafts,
    currentDraft,
    hasDraftContent,
    hasDraftChanged,
  };
};
